import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Rocket, TrendingUp } from 'lucide-react';
import SectionCard from './SectionCard';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { buildVendorProfileFromAverages } from '@/utils/vendorProfileBuilder';
import { getScoreColor } from '@/types/vendor-kpi';
import type { MyProgressStatsResponse } from '@/types/session';

type Props = {
  stats: MyProgressStatsResponse | null;
  loading: boolean;
};

export default function ProgressSummaryCard({ stats, loading }: Props) {
  const { t } = useTranslation();
  const profile = stats && stats.totalSessions > 0
    ? buildVendorProfileFromAverages(stats.kpiAveragesJson)
    : null;
  const categories = profile?.categories ?? [];
  const globalScore = profile?.globalScore ?? 0;
  const hasData = Boolean(profile) && (stats?.totalSessions ?? 0) > 0;

  return (
    <SectionCard
      title={t('dashboard.progressSummary.title', 'Resumen de progreso')}
      action={
        <Button asChild variant="ghost" size="sm">
          <Link to="/progress">
            {t('dashboard.progressSummary.cta', 'Ver progreso completo')}
            <ArrowRight className="size-3.5" />
          </Link>
        </Button>
      }
      contentClassName="space-y-5"
    >
      {loading ? (
        <>
          <Skeleton className="h-10 w-1/2" />
          <div className="space-y-2">
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
            <Skeleton className="h-3 w-full" />
          </div>
        </>
      ) : !hasData ? (
        <div className="flex flex-col items-center justify-center gap-2 py-8 text-center">
          <Rocket className="size-6 text-muted-foreground/50" />
          <p className="text-sm text-muted-foreground">
            {t('dashboard.progressSummary.empty', 'Completa tu primera simulación para ver tu progreso aquí')}
          </p>
        </div>
      ) : (
        <>
          <div className="flex items-end justify-between gap-4">
            <div>
              <div className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {t('dashboard.progressSummary.globalScore', 'Score global')}
              </div>
              <div className="mt-1 flex items-baseline gap-2">
                <span
                  className="text-5xl font-extrabold tabular-nums"
                  style={{ color: getScoreColor(globalScore) }}
                >
                  {globalScore}
                </span>
                <span className="text-sm text-muted-foreground">/ 99</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
              <TrendingUp className="size-3.5 text-primary" />
              {stats?.totalSessions ?? 0} {t('dashboard.progressSummary.sessionsLabel', 'sesiones')}
            </div>
          </div>

          <div className="space-y-2">
            {categories.map((cat) => (
              <div key={cat.id} className="flex items-center gap-2">
                <span
                  className="w-8 text-right text-sm font-extrabold tabular-nums"
                  style={{ color: getScoreColor(cat.score) }}
                >
                  {cat.score}
                </span>
                <span className="w-10 text-xs text-muted-foreground">{cat.shortName}</span>
                <div className="h-1.5 flex-1 overflow-hidden rounded-full bg-border">
                  <div
                    className="h-full rounded-full transition-all duration-500"
                    style={{
                      width: `${(cat.score / 99) * 100}%`,
                      backgroundColor: getScoreColor(cat.score),
                    }}
                  />
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </SectionCard>
  );
}
